// English texts of the "views" area. Keys start with "views."
export default {
  // View names (glossary: Gantt, Kanban and Markdown stay as they are)
  "views.name.dashboard": "Dashboard",
  "views.name.bubbles": "Bubbles",
  "views.name.bubble": "Bubble",
  "views.name.mindmap": "Mindmap",
  "views.name.gantt": "Gantt",
  "views.name.kanban": "Kanban",
  "views.name.markdown": "Markdown",
  "views.name.routines": "Routines",

  // Shared by several views
  "views.node": "Node",
  "views.expand": "Expand",
  "views.collapse": "Collapse",
  "views.loadingDots": "Loading...",
  "views.loadFailed": "Could not load this view — check your connection and switch to it again.",
  "views.viewOnly": "View Only",
  "views.tree.noData": "No data",
  // Breadcrumb name of a map root without a name
  "views.root": "Root",
  // Empty map drawn on the canvas
  "views.noItems": "No items yet",
  // Drag ghost when several selected nodes move
  "views.dragNodes_one": "{count} node",
  "views.dragNodes_other": "{count} nodes",

  // Keyboard hints under the views; keys are shown apart as <kbd>
  "views.hints.viewerMode": "Viewer mode - no modifications allowed",
  "views.hints.sidebar": "sidebar",
  "views.hints.add": "add",
  "views.hints.editName": "edit name",
  "views.hints.editProps": "edit props",
  "views.hints.collapse": "collapse",
  "views.hints.executionList": "execution list",
  "views.hints.routinesPanel": "routines panel",
  "views.hints.up": "up",
  "views.hints.delete": "delete",
  // {ctrl} is the Ctrl key
  "views.hints.ctrlDragReparent": "{ctrl}+drag reparent",
  "views.hints.ctrlDragRestructure": "{ctrl}+drag restructure",
  "views.hints.doubleClickZoom": "Double-click to zoom",
  // {keys} are the + and − keys, {alt} the Alt key
  "views.hints.zoomKeys": "{keys} or {alt}+scroll zoom",
  "views.hints.dragMove": "Drag to move",
  "views.hints.dragEdgesResize": "Drag edges to resize",
  "views.hints.dragCards": "Drag cards between columns",
  "views.hints.doubleClickEdit": "Double-click to edit",
  // Markdown view hints
  "views.hints.indent": "indent",
  "views.hints.outdent": "outdent",
  "views.hints.move": "move",
  "views.hints.notes": "notes",
  "views.hints.commands": "commands",
  "views.hints.headingEnter": "heading + Enter = new node",

  // Horizon (time filter of the planning views)
  "views.horizon.day_one": "{count} day",
  "views.horizon.day_other": "{count} days",
  "views.horizon.week_one": "{count} week",
  "views.horizon.week_other": "{count} weeks",
  "views.horizon.month_one": "{count} month",
  "views.horizon.month_other": "{count} months",
  "views.horizon.past.day_one": "Past {count} day",
  "views.horizon.past.day_other": "Past {count} days",
  "views.horizon.past.week_one": "Past {count} week",
  "views.horizon.past.week_other": "Past {count} weeks",
  "views.horizon.past.month_one": "Past {count} month",
  "views.horizon.past.month_other": "Past {count} months",
  "views.horizon.ahead.day_one": "Next {count} day",
  "views.horizon.ahead.day_other": "Next {count} days",
  "views.horizon.ahead.week_one": "Next {count} week",
  "views.horizon.ahead.week_other": "Next {count} weeks",
  "views.horizon.ahead.month_one": "Next {count} month",
  "views.horizon.ahead.month_other": "Next {count} months",
  "views.horizon.past.label": "Active within",
  "views.horizon.past.help": "Tasks created, edited, tagged, completed or worked on during the selected past period.",
  "views.horizon.ahead.label": "Due / planned within",
  "views.horizon.ahead.help": "Tasks due during the selected future period, or whose planned start-to-due span overlaps it.",
  "views.horizon.combinedHelp": "When both periods are limited, tasks matching either one are shown.",
  "views.horizon.anyTime": "Any time",
  "views.horizon.allTime": "All time",
  // {date} is a date written 2026-09-28
  "views.horizon.since": "Since {date}",
  "views.horizon.through": "Through {date}",
  "views.horizon.title": "Horizon",
  "views.horizon.now": "Now",
  "views.horizon.inList": "In daily list",
  "views.horizon.inListTitle": "Show only tasks in the current daily list",
  "views.horizon.reset": "Reset horizon",
  "views.horizon.resetTitle": "Show all time and clear list filters",
  "views.horizon.show": "Show horizon settings",
  "views.horizon.hide": "Hide horizon settings",
  "views.horizon.showActive": "Show horizon settings (filter active)",
  "views.horizon.hideActive": "Hide horizon settings (filter active)",
  // {side} is "Active within" or "Due / planned within", {period} e.g. "Past 3 days"
  "views.horizon.sliderValue": "{side}: {period}.",

  // Why a note is linked to a task (notes search)
  "views.noteReason.inList": "In execution list",
  "views.noteReason.taskReference": "Task reference",
  "views.noteReason.routineInList": "Routine in execution list",
  "views.noteReason.routineSession": "Routine session",
  "views.noteReason.titleMentioned": "Title mentioned",

  // Built-in tag categories (sidebar and filters)
  "views.tags.progress": "Progress",
  "views.tags.labels": "Labels",
  // The three built-in Progress tags (kanban columns); stored in English, shown translated
  "views.tags.todo": "Todo",
  "views.tags.inProgress": "In Progress",
  "views.tags.done": "Done",

  // Notes filtered by one task (notes panel, Notes view, mobile)
  "views.taskFilter.untitled": "Untitled task",
  "views.taskFilter.showAll": "Show all notes",
  "views.taskFilter.clear": "Clear task filter: {title}",
  "views.taskFilter.task": "Task: {title}",
  "views.taskFilter.count_one": "{count} note",
  "views.taskFilter.count_other": "{count} notes",
  "views.taskFilter.mentionsTitle": "Also find the task's current title in note titles and text",
  "views.taskFilter.mentions": "Include title matches",

  // Search of the open map (sidebar)
  "views.search.noMatches": "No matches",
  "views.search.statusTitle": "Matching tasks · Ctrl+G next · Ctrl+Shift+G previous",
  "views.search.empty": "No matching titles or notes.",
  "views.search.mapRoot": "Map root",
  "views.search.kindNote": "Note",
  "views.search.more_one": "{count} match · use the arrows to browse all",
  "views.search.more_other": "{count} matches · use the arrows to browse all",

  // Linked maps shown inside a map; {name} is the linked map's name
  "views.linked.label.linked": "Linked",
  "views.linked.label.loading": "Loading",
  "views.linked.label.noAccess": "No access",
  "views.linked.label.requested": "Requested",
  "views.linked.label.gone": "No longer online",
  "views.linked.label.offline": "Offline",
  "views.linked.label.conflict": "Open separately",
  "views.linked.loaded": "Linked: {name} — click to open",
  "views.linked.loadedUnnamed": "Linked: document — click to open",
  "views.linked.loading": "Loading linked document: {name}…",
  "views.linked.loadingUnnamed": "Loading linked document…",
  "views.linked.noAccess": "No access to \"{name}\". Click to request access.",
  "views.linked.noAccessUnnamed": "No access to \"this document\". Click to request access.",
  "views.linked.gone": "“{name}” is no longer online: it was deleted, or moved to a device. You can remove this link.",
  "views.linked.goneUnnamed": "This linked map is no longer online: it was deleted, or moved to a device. You can remove this link.",
  "views.linked.pending": "Access request pending for \"{name}\".",
  "views.linked.pendingUnnamed": "Access request pending for \"this document\".",
  "views.linked.nested": "Linked: {name} — open it to see its content",
  "views.linked.nestedUnnamed": "Linked: document — open it to see its content",
  "views.linked.offline": "\"{name}\" can't be reached. Showing the saved copy, read-only, until the connection comes back.",
  "views.linked.offlineUnnamed": "\"This linked document\" can't be reached. Showing the saved copy, read-only, until the connection comes back.",
  "views.linked.conflict": "\"{name}\" can't be shown inside this map (it is this map, or a copy with the same items). Click to open it on its own.",
  "views.linked.conflictUnnamed": "\"This document\" can't be shown inside this map (it is this map, or a copy with the same items). Click to open it on its own.",

  // Dashboard
  "views.dash.upcoming": "Upcoming Deadlines",
  "views.dash.inProgressTitle": "Tasks In Progress",
  "views.dash.calendar": "Calendar",
  "views.dash.configure": "Configure",
  "views.dash.calendarSetup": "Connect your calendars to see upcoming events",
  "views.dash.addCalendars": "Add Calendars",
  "views.dash.overview": "Overview",
  "views.dash.total": "Total",
  "views.dash.completed": "Completed",
  "views.dash.inProgress": "In Progress",
  "views.dash.todo": "To Do",
  "views.dash.percentComplete": "{percent}% Complete",
  "views.dash.bySection": "Progress by Section",
  "views.dash.noSections": "No sections",
  "views.dash.noUpcoming": "No upcoming deadlines",
  "views.dash.noInProgress": "No tasks in progress",
  // {days}: a number of days ("3d overdue")
  "views.dash.overdue": "{days}d overdue",
  "views.dash.inDays_one": "{count} day",
  "views.dash.inDays_other": "{count} days",
  "views.dash.dueToday": "Due today",
  "views.dash.dueTomorrow": "Due tomorrow",
  "views.dash.daysLeft": "{days}d left",
  // Execution card (from the daily lists)
  "views.dash.execution": "Execution",
  "views.dash.missing": "(missing)",
  "views.dash.currentList": "Current list",
  "views.dash.currentListItems_one": "Current list · {count} item",
  "views.dash.currentListItems_other": "Current list · {count} items",
  "views.dash.estimateTitle": "Sum of the items' estimates, minus days already worked on them",
  "views.dash.estimateLeft_one": "about {count} day of estimated work left",
  "views.dash.estimateLeft_other": "about {count} days of estimated work left",
  "views.dash.nothingListed": "nothing on the list",
  "views.dash.noBranch": "(no branch)",
  "views.dash.workedDaysRecent_one": "{count} worked day in the last 4 weeks",
  "views.dash.workedDaysRecent_other": "{count} worked days in the last 4 weeks",
  "views.dash.lastWeeks": "Where the last 4 weeks went",
  "views.dash.weeksGo": "Where the weeks go",
  "views.dash.weeksEmpty": "Tick \"worked on\" in a note's list to build this up.",
  "views.dash.workedPerWeek": "Worked days per week",
  // {week} is an ISO week such as 2026-W39
  "views.dash.weekTitle_one": "{week}: {count} worked day, {done} done",
  "views.dash.weekTitle_other": "{week}: {count} worked days, {done} done",
  "views.dash.signals": "Signals",
  "views.dash.starved_one": "— pinned, {count} period without a worked day",
  "views.dash.starved_other": "— pinned, {count} periods without a worked day",
  "views.dash.closedNoWork_one": "{count} item closed as done without a worked day",
  "views.dash.closedNoWork_other": "{count} items closed as done without a worked day",

  // Kanban
  "views.kanban.addColumn": "Add column",
  "views.kanban.moveLeft": "Move left",
  "views.kanban.moveRight": "Move right",
  "views.kanban.removeColumn": "Remove column",
  "views.kanban.noTasks": "No tasks",

  // Gantt
  "views.gantt.tasks": "Tasks",
  "views.gantt.resizeList": "Drag to resize the task list",
  "views.gantt.rowHeightUp": "Increase row height",
  "views.gantt.rowHeight": "Row height (% of default)",
  "views.gantt.rowHeightDown": "Decrease row height",
  "views.gantt.addTask": "+ Add Task",
  "views.gantt.zoomHint": "Alt+Scroll to zoom",
  "views.gantt.empty": "No dated or listed tasks in this focus.",
  // {date}: a date written 2026-09-28
  "views.gantt.todayAt": "Today: {date}",
  // Bar tooltips; {name} is the task name
  "views.gantt.unscheduled": "{name} — unscheduled: drag or resize to set dates",
  "views.gantt.planned": "{name} — planned",
  "views.gantt.workedOn": "Worked on {date}",
  "views.gantt.actual_one": "Actual: {count} day worked on",
  "views.gantt.actual_other": "Actual: {count} days worked on",
  "views.gantt.returnToList": "Return to daily list: {date}",
  "views.gantt.sessionRecorded": "Session recorded · {date}",
  "views.gantt.scheduledRoutine": "Scheduled routine · {date}",
  "views.gantt.openRoutine": "Open routine for {date}",

  // Mindmap
  "views.mindmap.layout.balanced": "balanced",
  "views.mindmap.layout.right": "right",
  "views.mindmap.layout.left": "left",
  // {mode} is one of the three layouts above
  "views.mindmap.layoutTitle": "Layout: {mode} (click to cycle balanced / right / left)",

  // Bubbles
  "views.bubble.emptyHint": "Click \"+ New Item\" to add children",
  "views.bubble.canvasError": "Canvas Error",
  "views.bubble.refresh": "Refresh page.",
  "views.bubble.fit": "Fit all to view",
  // Shown while dragging a bubble; CTRL is the keyboard key
  "views.bubble.restructureMode": "CTRL: Restructure mode - Drop to nest, drag out to eject",
  "views.bubble.moveMode": "Move mode - Hold CTRL to restructure",

  // Markdown view: formatting toolbar and slash commands
  "views.md.heading": "Heading {level}",
  "views.md.bold": "Bold",
  "views.md.italic": "Italic",
  "views.md.strike": "Strikethrough",
  "views.md.inlineCode": "Inline Code",
  "views.md.codeBlock": "Code Block",
  "views.md.bulletList": "Bullet List",
  "views.md.numberedList": "Numbered List",
  "views.md.quote": "Quote",
  "views.md.horizontalRule": "Horizontal Rule",
  "views.md.blockquote": "Blockquote",
  "views.md.clearFormatting": "Clear Formatting",
  "views.md.table": "Table",
  "views.md.noCommands": "No commands found",
  "views.md.insertImage": "Insert Image",
  "views.md.attachFile": "Attach File",
  "views.md.insertTable": "Insert Table",
  "views.md.clickCellFirst": "Click a cell to edit first",
  "views.md.uploadNotReady": "Cannot upload: document not ready",
  "views.md.uploadFailed": "Upload failed",
  "views.md.clickToEditFirst": "Click to edit first",
  // Attachments in notes and cells
  "views.md.externalImage": "External image",
  // Default file name of an attachment link without a name
  "views.md.attachment": "attachment",
  "views.md.audio": "Audio",
  "views.md.image": "Image",
  // {name}: a file or image name
  "views.md.failedToLoad": "{name} (failed to load)",
  "views.md.mathError": "Math rendering error",
  // Image toolbar in the editor
  "views.md.img.alignLeft": "Align left",
  "views.md.img.center": "Center",
  "views.md.img.alignRight": "Align right",
  "views.md.img.rotateLeft": "Rotate left 90°",
  "views.md.img.rotateRight": "Rotate right 90°",
  "views.md.img.caption": "Toggle caption",
  "views.md.img.remove": "Remove image from the note",
  "views.md.img.broken": "Broken image",
  "views.md.img.captionPlaceholder": "Caption...",

  // Markdown view: table toolbar
  "views.md.table.toggleHeader": "Toggle header row",
  "views.md.table.alignLeft": "Align left",
  "views.md.table.alignCenter": "Align center",
  "views.md.table.alignRight": "Align right",
  "views.md.table.rowAbove": "Add row above",
  "views.md.table.rowBelow": "Add row below",
  "views.md.table.deleteRow": "Delete row",
  "views.md.table.colLeft": "Add column left",
  "views.md.table.colRight": "Add column right",
  "views.md.table.deleteCol": "Delete column",
  "views.md.table.deleteTable": "Delete table",

  // Markdown view: display settings and states
  "views.md.ctrl.font": "Font",
  "views.md.ctrl.fontUp": "Increase font size",
  "views.md.ctrl.fontDown": "Decrease font size",
  "views.md.ctrl.space": "Space",
  "views.md.ctrl.spaceUp": "Increase line spacing",
  "views.md.ctrl.spaceDown": "Decrease line spacing",
  "views.md.ctrl.width": "Width",
  "views.md.ctrl.widthUp": "Increase cell width",
  "views.md.ctrl.widthDown": "Decrease cell width",
  "views.md.ctrl.indent": "Indent",
  "views.md.ctrl.on": "On",
  "views.md.ctrl.onTitle": "Show depth indentation",
  "views.md.ctrl.off": "Off",
  "views.md.ctrl.offTitle": "Flat layout",
  "views.md.empty": "No content to display",
  "views.md.dragReorder": "Drag to reorder",
  "views.md.dragToChangeLevel": "Drag cell to change level",
  "views.md.listItem": "List item",
  "views.md.deleteNodeConfirm": "Delete this node and all its children?",
  "views.md.viewOnlyMode": "View only mode",

  // Markdown view: export and import; {format} is PDF or HTML
  "views.md.export.prepare": "Prepare {format}",
  "views.md.export.rendering": "Rendering {format}…",
  "views.md.export.prepared": "{format} prepared",
  "views.md.export.done": "{format} exported",
  "views.md.export.failed": "{format} export failed: {error}",
  // Written into an exported LaTeX file in place of an image; {alt} is its description
  "views.md.export.latexImage": "[Image: {alt}]",
  "views.md.import.title": "Import Markdown ZIP",
  "views.md.import.noVault": "Cannot import: vault not available",
  "views.md.import.unpacking": "Unpacking Markdown…",
  "views.md.import.noMdFile": "No .md file found in zip",
  "views.md.import.noHeadings": "No headings found in the markdown",
  "views.md.import.defaultName": "Imported Document",
  "views.md.import.mapName": "Map name:",
  "views.md.import.warnings": "Import finished with warnings",
  "views.md.import.withAttachments": "Map and attachments imported",
  "views.md.import.created": "Created \"{name}\"",
  "views.md.import.createdWithImages_one": "Created \"{name}\" with {count} image",
  "views.md.import.createdWithImages_other": "Created \"{name}\" with {count} images",

  // Notes panel and Notes view
  "views.notes.new": "+ New",
  "views.notes.newTitle": "New Note",
  "views.notes.closeTitle": "Close (N)",
  "views.notes.resizeList": "Drag to resize the list",
  "views.notes.selectOrCreate": "Select or create a note",
  "views.notes.searchPaused": "Search paused: save the current note before switching.",
  "views.notes.deleteTitle": "Delete Note?",
  "views.notes.deleteConfirm": "Are you sure you want to delete this note? This action cannot be undone.",
  // {title}: the note title; {date}: when it was created, as in views.notes.dateAt
  "views.notes.deleteTitledConfirm": "Are you sure you want to delete \"{title}\" (created {date})? This action cannot be undone.",
  "views.notes.deleteDatedConfirm": "Are you sure you want to delete the note created {date}? This action cannot be undone.",
  "views.notes.deleted": "Note deleted",
  "views.notes.listLabel": "Document notes",
  // "+ New" is the button above (views.notes.new)
  "views.notes.empty": "No notes yet. Click \"+ New\" to create one.",
  // Name of a note without a title: "Sep 28, 2026 at 10:15 AM"
  "views.notes.dateAt": "{date} at {time}",
  "views.notes.rename": "Rename note",
  "views.notes.delete": "Delete note",
  "views.notes.titlePlaceholder": "Note title…",
  "views.notes.titleLabel": "Note title",
  "views.notes.editingTitled": "Editing: {title} • {date}",
  "views.notes.editing": "Editing note • {date}",
  "views.notes.saving": "Saving...",
  "views.notes.clickToEdit": "Click to edit...",
  "views.notes.refnoteView": "Click to view {name}",
  "views.notes.refnoteRemove": "Remove refnote",
  "views.notes.fontSmaller": "Smaller font",
  "views.notes.fontLarger": "Larger font",
  "views.notes.mono": "Toggle monospace font",
  "views.notes.insertTable": "Insert table",
  // A refnote links a note to a task; {name} is the task name
  "views.notes.refnoteRemoved": "Removed refnote: {name}",
  "views.notes.refnoteAdded": "Added refnote: {name}",

  // Search of the notes (notes panel and Notes view)
  "views.notesSearch.label": "Search document notes",
  "views.notesSearch.placeholder": "Search all doc notes…",
  "views.notesSearch.relatedPlaceholder": "Search related notes…",
  "views.notesSearch.inputLabel": "Search all document notes",
  "views.notesSearch.clearLabel": "Clear note search",
  "views.notesSearch.clear": "Clear search",
  "views.notesSearch.prevLabel": "Previous note match",
  "views.notesSearch.prev": "Previous match (Ctrl+Shift+G)",
  "views.notesSearch.nextLabel": "Next note match",
  "views.notesSearch.next": "Next match (Ctrl+G)",
  // Name of the open map when the note list has no document names
  "views.notesSearch.currentMap": "Current map",
  "views.notesSearch.statusTitle_one": "{count} matching note · Ctrl+G next · Ctrl+Shift+G previous",
  "views.notesSearch.statusTitle_other": "{count} matching notes · Ctrl+G next · Ctrl+Shift+G previous",
  "views.notesSearch.resultsLabel": "Matching document notes",
  "views.notesSearch.matches_one": "{count} match",
  "views.notesSearch.matches_other": "{count} matches",
  "views.notesSearch.noRelatedMatch": "No related notes match this search.",
  "views.notesSearch.noLinked": "No linked notes yet. Add this task to a note’s execution list, or include title matches.",
  "views.notesSearch.empty": "No matching note titles or text.",
};
